import { ArrowLeft, Printer } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { TimeChart } from "../../charts/TimeChart";
import { SERIES } from "../../charts/palette";
import { fmt, PRODUCT_LABEL, productCode, when } from "../../lib/format";
import { useReport } from "../../lib/queries";
import { Button } from "../../ui/Button";
import { ErrorState, PageSkeleton } from "../../ui/Feedback";
import { Input } from "../../ui/Form";
import styles from "./Document.module.css";

const LEVEL: Record<string, string> = { ok: "Aman", low: "Tinggal ±30%", critical: "Tinggal ±10%", empty: "Habis" };
const monthName = (month: string) => new Intl.DateTimeFormat("id-ID", { month: "long", year: "numeric" }).format(new Date(`${month}-15T12:00:00`));

/**
 * Laporan pemakaian bulanan — what the catalog promises with the add-on
 * ("Laporan bulanan: pertanyaan yang paling sering ditanyakan") and with
 * every top-up. Printed or saved as PDF and sent to the client's PIC.
 */
export function ReportPage() {
  const { projectId = "" } = useParams();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const report = useReport(projectId, month);

  if (report.isPending) return <PageSkeleton />;
  if (report.isError) return <ErrorState error={report.error} />;
  const r = report.data;
  const avgPerConversation = r.totals.conversations ? r.totals.costMilli / r.totals.conversations : 0;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar} data-print-hide>
        <Link to={`/projects/${projectId}`}>
          <Button variant="ghost" icon={<ArrowLeft aria-hidden />}>
            Kembali ke proyek
          </Button>
        </Link>
        <div className={styles.toolbarRight}>
          <Input type="month" value={month} onChange={(event) => event.target.value && setMonth(event.target.value)} aria-label="Bulan laporan" style={{ width: 180 }} />
          <Button variant="primary" icon={<Printer aria-hidden />} onClick={() => window.print()}>
            Cetak / PDF
          </Button>
        </div>
      </div>

      <article className={styles.document} style={{ opacity: report.isPlaceholderData ? 0.6 : 1 }}>
        <header className={styles.docHeader}>
          <div>
            <div className={styles.docKicker}>Laporan Asisten AI · Navibiz</div>
            <h1>{r.project.name}</h1>
            <p>Pemakaian {monthName(r.month)}</p>
          </div>
          <dl className={styles.docMeta}>
            <dt>Klien</dt>
            <dd>{r.project.clientName}</dd>
            <dt>Layanan</dt>
            <dd>{r.project.packageCode ? `Website ${r.project.packageCode} + ${productCode(r.project.product)}` : PRODUCT_LABEL[r.project.product]}</dd>
            <dt>Periode</dt>
            <dd>
              {when.date(r.range.from)} – {when.date(r.range.to)}
            </dd>
          </dl>
        </header>

        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <span>Percakapan</span>
            <strong>{fmt.int(r.totals.conversations)}</strong>
          </div>
          <div className={styles.kpi}>
            <span>Pertanyaan dijawab</span>
            <strong>{fmt.int(r.totals.messages)}</strong>
          </div>
          <div className={styles.kpi}>
            <span>Dialihkan ke WhatsApp</span>
            <strong>{fmt.int(r.totals.handoffs)}</strong>
          </div>
          <div className={styles.kpi}>
            <span>Saldo terpakai</span>
            <strong>{fmt.rupiah(r.totals.costMilli)}</strong>
          </div>
        </div>

        <section className={styles.section}>
          <h2>Percakapan per hari</h2>
          <TimeChart kind="area" data={r.series} series={[{ key: "conversations", label: "Percakapan", color: SERIES.blue }]} format={fmt.int} height={180} ariaLabel="Percakapan per hari" />
        </section>

        <section className={styles.section}>
          <h2>Pertanyaan yang paling sering ditanyakan</h2>
          {r.topQuestions.length === 0 ? (
            <p>Belum ada pertanyaan bulan ini.</p>
          ) : (
            <table className={styles.simpleTable}>
              <thead>
                <tr>
                  <th>Topik</th>
                  <th>Contoh pertanyaan pengunjung</th>
                  <th className={styles.r}>Jumlah</th>
                </tr>
              </thead>
              <tbody>
                {r.topQuestions.map((cluster) => (
                  <tr key={cluster.label}>
                    <td>
                      <strong>{cluster.label}</strong>
                      {!cluster.entryId && <div style={{ color: "#9a3b13", fontSize: 12 }}>belum ada jawabannya — disarankan ditambahkan</div>}
                    </td>
                    <td>{cluster.examples.map((example) => `“${example}”`).join(", ")}</td>
                    <td className={styles.r}>{fmt.int(cluster.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {r.linkage && (
          <section className={styles.section}>
            <h2>Rujukan (Linkage)</h2>
            <p style={{ marginBottom: 12 }}>
              Chatbot menyarankan rujukan {fmt.int(r.linkage.offered)} kali bulan ini, dan pengunjung mengkliknya {fmt.int(r.linkage.clicks)} kali
              {r.linkage.offered ? ` (${fmt.percent(r.linkage.clicks / r.linkage.offered)})` : ""}.
            </p>
            {r.linkage.top.length > 0 && (
              <table className={styles.simpleTable}>
                <thead>
                  <tr>
                    <th>Rujukan yang paling sering diklik</th>
                    <th className={styles.r}>Ditawarkan</th>
                    <th className={styles.r}>Diklik</th>
                  </tr>
                </thead>
                <tbody>
                  {r.linkage.top.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.title ?? "(rujukan sudah dihapus)"}</strong>
                        {row.url && <div style={{ color: "#6e727a", fontSize: 12 }}>{row.url.replace(/^https?:\/\//, "")}</div>}
                      </td>
                      <td className={styles.r}>{fmt.int(row.offered)}</td>
                      <td className={styles.r}>{fmt.int(row.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {r.linkage.broken.length > 0 && (
              <>
                <p style={{ margin: "14px 0 8px", fontWeight: 600, color: "#9a3b13" }}>Tautan yang perlu diperbaiki</p>
                <table className={styles.simpleTable}>
                  <tbody>
                    {r.linkage.broken.map((row) => (
                      <tr key={row.url}>
                        <td>
                          <strong>{row.title}</strong>
                          <div style={{ color: "#6e727a", fontSize: 12 }}>{row.url.replace(/^https?:\/\//, "")}</div>
                        </td>
                        <td>{row.status === "broken" ? "Halaman tidak ditemukan" : "Bagian halaman tidak ditemukan"}{row.statusDetail ? ` (${row.statusDetail})` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>
        )}

        <section className={styles.section}>
          <h2>Saldo AI</h2>
          <table className={styles.simpleTable}>
            <tbody>
              <tr>
                <td>Saldo saat ini</td>
                <td className={styles.r}>
                  <strong>{fmt.rupiah(r.saldo.balanceMilli)}</strong> · {LEVEL[r.saldo.level]}
                  {r.saldo.daysLeft !== null && r.saldo.daysLeft > 0 ? ` · perkiraan cukup ±${r.saldo.daysLeft} hari` : ""}
                </td>
              </tr>
              <tr>
                <td>Rata-rata per percakapan</td>
                <td className={styles.r}>{fmt.rupiah(avgPerConversation)}</td>
              </tr>
              {r.ledger.map((row, i) => (
                <tr key={i}>
                  <td>
                    {row.kind === "topup" ? "Top-up" : row.kind === "refund" ? "Pengembalian" : "Koreksi"} {when.date(row.createdAt)}
                    {row.reference ? ` · ${row.reference}` : ""}
                  </td>
                  <td className={styles.r}>
                    {fmt.rupiah(row.amountMilli)}
                    {row.serviceFeeMilli ? ` (+ biaya layanan v3 ${fmt.rupiah(row.serviceFeeMilli)})` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: 12, fontSize: 12.5, color: "#6e727a" }}>
            Saldo dipotong sesuai pemakaian nyata: jumlah token × harga resmi penyedia AI × kurs JISDOR × faktor harga saldo{" "}
            {String(r.rates.saldoFactor).replace(".", ",")} (Lampiran III Pasal 4). Top-up kapan saja, tanpa biaya layanan. Riwayat rinci dapat diminta kapan saja.
          </p>
        </section>
      </article>
    </div>
  );
}
